import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

function statusColor(s) {
  if (s === 'active') return 'success';
  return 'default';
}

export default function TenantApiKeyDetailPage() {
  const { keyId } = useParams();
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [secretDialog, setSecretDialog] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editScopes, setEditScopes] = useState('');
  const [editExpires, setEditExpires] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/api-keys/${keyId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setApiKey(json.apiKey);
      setUsageSummary(json.usageSummary || null);
    } catch (e) {
      setError(e.message);
      setApiKey(null);
      setUsageSummary(null);
    } finally {
      setLoading(false);
    }
  }, [keyId]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    if (!apiKey) return;
    setEditName(apiKey.key_name || '');
    setEditScopes((apiKey.scopes || []).join(' '));
    const ex = apiKey.expires_at;
    if (ex) {
      const d = new Date(ex);
      const pad = (n) => String(n).padStart(2, '0');
      setEditExpires(
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      );
    } else setEditExpires('');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        key_name: editName.trim(),
        scopes: editScopes
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      };
      if (editExpires) body.expires_at = new Date(editExpires).toISOString();
      else body.expires_at = null;
      const res = await tenantApiFetch(`/api/tenant/api-keys/${keyId}`, { method: 'PATCH', body });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Update failed');
      setApiKey(json.apiKey);
      setEditOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/api-keys/${keyId}/revoke`, { method: 'POST', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Revoke failed');
      setApiKey(json.apiKey);
      setConfirmRevoke(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/api-keys/${keyId}/rotate`, { method: 'POST', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Rotate failed');
      setSecretDialog({ secret: json.secretShownOnce, newId: json.apiKey?.id });
      setConfirmRotate(false);
      if (json.apiKey?.id && String(json.apiKey.id) !== String(keyId)) {
        navigate(`/tenant/api-keys/${json.apiKey.id}`, { replace: true });
      } else {
        await load();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!secretDialog?.secret) return;
    try {
      await navigator.clipboard.writeText(secretDialog.secret);
    } catch (_e) {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !apiKey) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!apiKey) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{apiKey.key_name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {apiKey.masked_key}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/tenant/api-keys')}>
            Back to list
          </Button>
          <TenantCan permission="tenant.api_keys.manage">
            {apiKey.status === 'active' && (
              <>
                <Button variant="outlined" onClick={openEdit}>
                  Edit settings
                </Button>
                <Button color="warning" variant="outlined" onClick={() => setConfirmRotate(true)} disabled={busy}>
                  Rotate
                </Button>
                <Button color="error" variant="outlined" onClick={() => setConfirmRevoke(true)} disabled={busy}>
                  Revoke
                </Button>
              </>
            )}
          </TenantCan>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('ID', apiKey.id)}
          {row('Tenant ID', apiKey.tenant_id)}
          {row('Key prefix', apiKey.key_prefix)}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip size="small" label={apiKey.status} color={statusColor(apiKey.status)} />
            </Box>
          </Grid>
          {row(
            'Scopes',
            Array.isArray(apiKey.scopes) && apiKey.scopes.length ? apiKey.scopes.join(', ') : '—'
          )}
          {row('Expires', apiKey.expires_at ? new Date(apiKey.expires_at).toLocaleString() : 'Never')}
          {row('Last used', apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString() : '—')}
          {row('Revoked', apiKey.revoked_at ? new Date(apiKey.revoked_at).toLocaleString() : '—')}
          {row(
            'Created by',
            apiKey.created_by?.email ? `${apiKey.created_by.email} (#${apiKey.created_by.id})` : '—'
          )}
          {row('Created', apiKey.created_at ? new Date(apiKey.created_at).toLocaleString() : '—')}
          {row('Updated', apiKey.updated_at ? new Date(apiKey.updated_at).toLocaleString() : '—')}
          {apiKey.rotated_from_id != null ? row('Rotated from ID', apiKey.rotated_from_id) : null}
        </Grid>
      </Paper>

      {usageSummary && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Usage summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {usageSummary.note}
          </Typography>
          <Typography variant="body2">
            Last 30 days (tenant rollup):{' '}
            <strong>{usageSummary.last_30d_api_requests ?? '—'}</strong> API requests
            {usageSummary.last_30d_auth_logins_rollups != null && (
              <>
                {' '}
                · <strong>{usageSummary.last_30d_auth_logins_rollups}</strong> auth login rollups
              </>
            )}
          </Typography>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Audit events for create, update, revoke, and rotate are written to the tenant audit log when those actions
        occur.
      </Typography>

      <Dialog open={confirmRevoke} onClose={() => !busy && setConfirmRevoke(false)}>
        <DialogTitle>Revoke API key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Authentications with this key will fail immediately. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={revoke} disabled={busy}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRotate} onClose={() => !busy && setConfirmRotate(false)}>
        <DialogTitle>Rotate API key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The current key will be revoked and replaced. You will see the new secret once; save it before closing
            the dialog.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRotate(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={rotate} disabled={busy}>
            Rotate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(secretDialog)} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>New secret (shown once)</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={secretDialog?.secret || ''}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={copySecret}>
            Copy
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSecretDialog(null);
              load();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !busy && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit API key settings</DialogTitle>
        <DialogContent>
          <TextField
            label="Key name"
            fullWidth
            margin="normal"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
          <TextField
            label="Scopes"
            fullWidth
            margin="normal"
            value={editScopes}
            onChange={(e) => setEditScopes(e.target.value)}
            helperText="Space or comma separated"
          />
          <TextField
            label="Expires at"
            type="datetime-local"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={editExpires}
            onChange={(e) => setEditExpires(e.target.value)}
            helperText="Clear by removing the value and saving (leave empty) — use Edit to set null"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy || !editName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
